/**
 * `<CreateLinkBlockButton/>` — the "+ link block" affordance on the canvas
 * (round-4 ⑥, #1043).
 *
 * Opens a tiny inline title input; on submit (Enter or "Add") fires the
 * parent's `onCreate(title)` so the canvas can compute the viewport-centre
 * coordinates and call `useCreateLinkBlock`. A `link_block` is an OFF-TREE
 * freeform annotation — about the WORK, never a person (§A9) — so the copy
 * stays calm + factual ("+ link block", "What to label this annotation?").
 *
 * The widget collapses to its compact "+ link block" button by default; it
 * expands only after a click. Esc collapses; click-out collapses; a successful
 * submit collapses too. The trigger button stops pointer propagation so the
 * canvas's pan/pointer-capture never swallows the click (the same #959 fix
 * the zoom widget + connector port apply).
 */

import { useEffect, useRef, useState } from "react";

import { useMapTheme } from "../theme/MapThemeContext";

export interface CreateLinkBlockButtonProps {
  /** Fired when the lead submits a non-empty trimmed title. */
  onCreate: (title: string) => void;
}

export function CreateLinkBlockButton({ onCreate }: CreateLinkBlockButtonProps) {
  const theme = useMapTheme();
  const c = theme.palette;
  const editorial = theme.mode === "editorial";

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Focus the input on open so the lead can start typing.
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // Esc cancels; outside-click cancels (same calm dismissal the other inline
  // affordances use).
  useEffect(() => {
    if (!open) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") {
        setOpen(false);
        setDraft("");
      }
    };
    const onDocClick = (ev: MouseEvent) => {
      const el = containerRef.current;
      if (el && !el.contains(ev.target as Node)) {
        setOpen(false);
        setDraft("");
      }
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onDocClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onDocClick);
    };
  }, [open]);

  const submit = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    onCreate(trimmed);
    setDraft("");
    setOpen(false);
  };

  return (
    <div ref={containerRef} data-testid="create-link-block">
      {open ? (
        <div
          className="flex items-center gap-1.5 rounded-md border bg-white p-1.5 shadow-md"
          style={{ borderColor: c.softBorder }}
        >
          <input
            ref={inputRef}
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submit();
              }
            }}
            placeholder="What to label this annotation?"
            data-testid="create-link-block-input"
            className="w-44 rounded border px-2 py-1 text-xs outline-none"
            style={{ borderColor: c.softBorder, color: c.ink }}
          />
          <button
            type="button"
            data-testid="create-link-block-submit"
            onClick={submit}
            disabled={draft.trim().length === 0}
            className="rounded border px-2 py-1 text-[11px] transition-colors hover:bg-slate-50 disabled:opacity-50"
            style={{ borderColor: c.softBorder, color: c.ink }}
          >
            Add
          </button>
        </div>
      ) : (
        <button
          type="button"
          data-testid="create-link-block-trigger"
          onClick={() => setOpen(true)}
          className="rounded-md border bg-white px-2.5 py-1.5 text-[11px] shadow-sm transition-colors hover:bg-slate-50"
          style={{
            borderColor: c.softBorder,
            color: c.muted,
            fontFamily: editorial ? theme.fonts.mono : undefined,
            letterSpacing: editorial ? "0.04em" : undefined,
          }}
        >
          + link block
        </button>
      )}
    </div>
  );
}
